/**
 * PageMeta — per-page SEO meta tags via react-helmet-async.
 *
 * Drop this at the top of any page component:
 *
 *   <PageMeta
 *     title="Journal Title"
 *     description="The first 160 chars of the journal…"
 *     image="/og-image.png"
 *     canonical="https://writevellum.com/journals/alice/my-post"
 *     type="article"
 *     publishedAt="2025-01-15T10:00:00Z"
 *     author="Alice Example"
 *     jsonLd={buildArticleJsonLd(journal, canonicalUrl)}
 *   />
 *
 * Design notes:
 *  - All props are optional except (indirectly) title/description via defaults.
 *  - `jsonLd` accepts either a single object or an array; each becomes its own
 *    `<script type="application/ld+json">` tag. react-helmet-async handles
 *    de-duplication when the same page rerenders.
 *  - No runtime access to `window` — callers pass an absolute `canonical`
 *    URL built by seo.ts helpers, which are SSR-safe.
 *  - OG image URLs are resolved to absolute form before emission. Facebook,
 *    Twitter, LinkedIn, and other OG consumers all require absolute URLs;
 *    a relative path like "/og-image.png" is silently ignored. When a caller
 *    passes a relative URL we join it against the canonical URL's origin.
 *    For journal pages this means: if the author set a cover image, that
 *    (already-absolute) URL is used as the og:image; if not, we fall back
 *    to the Vellum default and upgrade it to an absolute URL here.
 */
import { Helmet } from 'react-helmet-async';
import { getSiteOrigin } from '@/utils/seo';

const SITE_NAME = 'Vellum';
const DEFAULT_DESCRIPTION =
  'Vellum — tier-gated long-form writing for creators. Publish, monetise, and connect.';
const DEFAULT_IMAGE = '/og-image.png';

interface PageMetaProps {
  /** Page title — appended with "· Vellum". Pass empty string for the home page. */
  title?: string;
  description?: string;
  image?: string;
  /** Canonical URL for this page. Must be absolute (include protocol + host). */
  canonical?: string;
  /** Set to 'article' for journal reader pages */
  type?: 'website' | 'article';
  /** ISO 8601 publish date (for articles) */
  publishedAt?: string;
  /** ISO 8601 last-modified date (for articles). Rendered as article:modified_time. */
  modifiedAt?: string;
  /** Author display name (for articles) */
  author?: string;
  /** Prevent search engine indexing (drafts, private pages, auth pages) */
  noIndex?: boolean;
  /**
   * JSON-LD structured data. Pass either a single schema.org object (e.g. the
   * output of `buildArticleJsonLd`) or an array of them. Each is serialised
   * into its own `<script type="application/ld+json">` tag in <head>.
   *
   * Strings inside the object are escaped by `JSON.stringify` before being
   * placed inside the `<script>`, which is the canonical safe pattern for
   * JSON-LD (XSS-safe because the script type prevents HTML parsing).
   */
  jsonLd?: Record<string, unknown> | Record<string, unknown>[];
}

/**
 * Turn a possibly-relative image URL into an absolute one suitable for OG
 * tags. Preference order:
 *   1. Already absolute (http:// or https://) → use as-is.
 *   2. Protocol-relative (//cdn.example/img.png) → prepend https:.
 *   3. Root-relative (/og-image.png) → join with canonical's origin if
 *      available, otherwise with getSiteOrigin() (PRODUCTION_URL in SSR,
 *      window.location.origin in the browser).
 *   4. Anything else → return undefined so the tag is omitted rather than
 *      emitting a malformed URL.
 */
function toAbsoluteImageUrl(
  raw: string | undefined,
  canonical: string | undefined,
): string | undefined {
  if (!raw) return undefined;
  const trimmed = raw.trim();
  if (!trimmed) return undefined;

  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (trimmed.startsWith('//')) return `https:${trimmed}`;

  // Determine the origin to join against.
  let origin: string | null = null;
  if (canonical) {
    try {
      origin = new URL(canonical).origin;
    } catch {
      /* fall through */
    }
  }
  if (!origin) {
    try {
      origin = getSiteOrigin();
    } catch {
      /* no SSR fallback available */
    }
  }
  if (!origin) return undefined;

  if (trimmed.startsWith('/')) return `${origin}${trimmed}`;
  // Bare filename or relative path — treat as root-relative.
  return `${origin}/${trimmed.replace(/^\.?\/*/, '')}`;
}

export function PageMeta({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  canonical,
  type = 'website',
  publishedAt,
  modifiedAt,
  author,
  noIndex = false,
  jsonLd,
}: PageMetaProps): JSX.Element | null {
  const fullTitle = title ? `${title} · ${SITE_NAME}` : SITE_NAME;
  const desc = description.slice(0, 160); // OG description limit

  const jsonLdArray = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  // Resolve og:image to an absolute URL. Callers pass either a cover image
  // (already absolute, from S3 / CDN) or leave it unset to inherit DEFAULT_IMAGE;
  // either way the emitted tag will have a full https://… URL.
  const ogImage = toAbsoluteImageUrl(image, canonical);

  return (
    <Helmet>
      <title>{fullTitle}</title>
      <meta name="description" content={desc} />
      {noIndex && <meta name="robots" content="noindex, nofollow" />}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Open Graph */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={desc} />
      {ogImage && <meta property="og:image" content={ogImage} />}
      <meta property="og:site_name" content={SITE_NAME} />
      {canonical && <meta property="og:url" content={canonical} />}

      {/* Article metadata */}
      {type === 'article' && publishedAt && (
        <meta property="article:published_time" content={publishedAt} />
      )}
      {type === 'article' && modifiedAt && (
        <meta property="article:modified_time" content={modifiedAt} />
      )}
      {type === 'article' && author && <meta property="article:author" content={author} />}

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={desc} />
      {ogImage && <meta name="twitter:image" content={ogImage} />}

      {/* Structured data (JSON-LD). One <script> per schema object. */}
      {jsonLdArray.map((schema, idx) => (
        <script key={`ld-${idx}`} type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      ))}
    </Helmet>
  );
}

export default PageMeta;
